import { CloseButton, Menu, Table } from '@mantine/core';
import {
	IconChevronDown,
	IconChevronUp,
	IconInfoCircle,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AddressModal } from '../../../components/AddressModal';
import { NoDataFoundTableRow } from '../../../components/DataTable';
import { LoadingRows } from '../../../components/LoadingRows';
import { TableRowCollapsible } from '../../../components/TableRowCollapsible';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { Title } from '../../../components/Title';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { query } from '../../../util/api/query';
import { formatToCurrency, formatToDateString } from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { OrderDetailDto, OrderDto } from '../types/OrderDto';
import { KeyedRow } from '../../../components/KeyedRow';

interface OrderRowProps {
	order: OrderDto;
}

export function OrderRow({ order }: OrderRowProps) {
	const { organisation } = useParams();

	const [orderOpen, setOrderOpen] = useState(false);
	const [addressOpen, setAddressOpen] = useState(false);

	const orderDetailsQuery = query<OrderDetailDto[]>(
		`${organisation}/orders/details/${order.orderNumber}`,
		orderOpen,
	);

	const isDataFound = useMemo(() => {
		return (
			orderDetailsQuery.data?.value && orderDetailsQuery.data?.value.length > 0
		);
	}, [orderDetailsQuery.data?.value]);

	return (
		<>
			<AddressModal
				title={`Order ${order.orderNumber}`}
				isOpen={addressOpen}
				close={() => setAddressOpen(false)}
			/>
			<KeyedRow>
				<Table.Td>{formatToDateString(order.dateCreated)}</Table.Td>
				<Table.Td>{undefinedCheck(order.orderNumber)}</Table.Td>
				<Table.Td>{order.createdBy}</Table.Td>
				<Table.Td>{undefinedCheck(order.branchName)}</Table.Td>
				<Table.Td>{formatToCurrency(order.totalNet)}</Table.Td>
				<Table.Td>{formatToCurrency(order.totalVat)}</Table.Td>
				<Table.Td>{order.orderStatus}</Table.Td>
				<Table.Td>
					{order.dateReceived ? (
						formatToDateString(order.dateReceived)
					) : (
						<i>Undefined</i>
					)}
				</Table.Td>
				<Table.Td>
					<CloseButton
						icon={
							!orderOpen ? (
								<IconChevronDown {...defaultIconStyle} />
							) : (
								<IconChevronUp {...defaultIconStyle} />
							)
						}
						onClick={() => setOrderOpen(!orderOpen)}
					/>
				</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconInfoCircle {...defaultIconStyle} />}
							onClick={() => setAddressOpen(true)}
						>
							View Address
						</Menu.Item>
						{/* <Menu.Item
								leftSection={<IconSend {...iconStyle} />}
								onClick={() => {}}
							>
								Mark Delivered
							</Menu.Item> 
							
							ASK ABOUT THIS!

							*/}
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
			<TableRowCollapsible isOpen={orderOpen}>
				<Table>
					<Table.Thead>
						<KeyedRow>
							<Table.Th>
								<Title label={'SKUID'} />
							</Table.Th>
							<Table.Th>
								<Title label={'Description'} />
							</Table.Th>
							<Table.Th>
								<Title label={'Net Value'} />
							</Table.Th>
							<Table.Th>
								<Title label={'VAT'} />
							</Table.Th>
							<Table.Th>
								<Title label={'Case Cost'} />
							</Table.Th>
							<Table.Th>
								<Title label={'Case Qty.'} />
							</Table.Th>
							<Table.Th />
						</KeyedRow>
					</Table.Thead>
					<Table.Tbody>
						{!orderDetailsQuery.isLoading ? (
							<>
								{!isDataFound && <NoDataFoundTableRow />}
								{orderDetailsQuery.data?.value.map((orderDetail) => (
									<>
										<KeyedRow>
											<Table.Td>{undefinedCheck(orderDetail.skuid)}</Table.Td>
											<Table.Td>
												{undefinedCheck(orderDetail.description)}
											</Table.Td>
											<Table.Td>
												{formatToCurrency(orderDetail.netValue)}
											</Table.Td>
											<Table.Td>{formatToCurrency(orderDetail.vat)}</Table.Td>
											<Table.Td>
												{formatToCurrency(orderDetail.caseCost)}
											</Table.Td>
											<Table.Td>{orderDetail.caseQuantity}</Table.Td>
										</KeyedRow>
									</>
								))}
							</>
						) : (
							<LoadingRows rows={1} />
						)}
					</Table.Tbody>
				</Table>
			</TableRowCollapsible>
		</>
	);
}
